import { Divider, makeStyles, Typography, useTheme } from '@material-ui/core';
import { IconButton, DaysList, GreenSwitch, DatesList } from '@coolremote/shared';
import clsx from 'clsx';
import React from 'react';
import { t } from 'ttag';
import { symbols } from '../../constants/sensorsUnitsSymbols';
import DeleteIcon from '@icons/DeleteIcon';
import EditIcon from '@icons/EditIcon';
import Sync from '@icons/Sync';
import { useStoreState } from '../../models/RootStore';
import { sensorMeasurementUnits } from '@models/Sensors';
import { minsToTime } from '../../services/timeService';
import Tooltip from '../Tooltip/LightTooltip';
import styles from './scheduleList.style';

// ---- Types ----

interface CommonItemProps {
  type: 'unit' | 'sensor' | 'group' | 'system';
  schedule: any;
  onEdit: (scheduleId: string) => void;
  onDeleteClick: (schedule: any, type: string) => void;
  onToggle: (e: any, scheduleId: string) => void;
}

interface UnitLikeScheduleItemProps extends CommonItemProps {
  scheduleId: string;
  isGroup: boolean;
  enableSetpointControl: boolean;
  isHalfCDegreeEnabled?: boolean;
  notEditable?: boolean;
}

interface UnitScheduleItemProps extends UnitLikeScheduleItemProps {
  type: 'unit';
}

interface GroupScheduleItemProps extends UnitLikeScheduleItemProps {
  type: 'group';
}

interface SystemScheduleItemProps extends UnitLikeScheduleItemProps {
  type: 'system';
}

interface SensorScheduleItemProps extends CommonItemProps {
  type: 'sensor';
  sensor: any;
}
interface BacnetScheduleItemProps {
  type: 'bacnet';
  schedule: any;
  onEdit: (scheduleId: string) => void;
  onDeleteClick: (schedule: any, type: string) => void;
  onToggle: (e: any, scheduleId: string) => void;
  isDesktop?: boolean;
}

interface ModbusScheduleItemProps {
  type: 'modbus';
  schedule: any;
  onEdit: (scheduleId: string) => void;
  onDeleteClick: (schedule: any, type: string) => void;
  onToggle: (e: any, scheduleId: string) => void;
  isDesktop?: boolean;
}

type ScheduleItemProps =
  | UnitScheduleItemProps
  | GroupScheduleItemProps
  | SystemScheduleItemProps
  | SensorScheduleItemProps
  | BacnetScheduleItemProps
  | ModbusScheduleItemProps;

// ---- Component ----

const useStyles = makeStyles(styles);

const ScheduleItem: React.FC<ScheduleItemProps> = (props) => {
  const theme : any = useTheme();
  const { type, schedule, onEdit, onDeleteClick, onToggle } = props;
  const isSensor = type === 'sensor';
  const isBacnet = type === 'bacnet';
  const isModbus = type === 'modbus';
  // BACnet and Modbus schedules use generic protocol-object rendering:
  // no fixed off-time, NG-sync indicator, or setpoint value.
  const isProtocolSchedule = isBacnet || isModbus;
  const classes = useStyles();

  const { timeFormat, dateFormat } = useStoreState((state) => state.users);
  const temperatureSymbol = useStoreState(
    (state) => state.users.getTemperatureScaleDisplay
  );
  const {
    scheduleCategories,
    weekDays,
    sensorTypes: fullSensorTypes,
  } = useStoreState((state) => state.types);

  if (!schedule) {
    return null;
  }

  const {
    powerOnTime,
    powerOffTime,
    days = [],
    dates = [],
    isDisabled,
    scheduleCategory,
    isSynced,
    permissions,
  } = schedule;

  // ---- Type-specific values ----

  const scheduleId =
    isSensor || isProtocolSchedule
      ? schedule.id
      : (props as UnitLikeScheduleItemProps).scheduleId;

  const canUpdateSchedules = (permissions as any).canUpdate;
  const canDeleteSchedules = (permissions as any).canDelete;

  const notEditable =
    !isProtocolSchedule &&
    (type === 'group' ||
      type === 'system' ||
      !!(props as UnitLikeScheduleItemProps).notEditable);
  const groupSchedule = type === 'group';
  const systemSchedule = type === 'system';
  const isGroup = !isSensor && !!(props as UnitLikeScheduleItemProps).isGroup;
  const enableSetpointControl =
    !isSensor && !!(props as UnitLikeScheduleItemProps).enableSetpointControl;
  const isHalfCDegreeEnabled =
    !isSensor && !!(props as UnitLikeScheduleItemProps).isHalfCDegreeEnabled;
  const { setpoint } = schedule;

  // Sensor-only
  const sensor = isSensor ? (props as SensorScheduleItemProps).sensor : null;
  const { sensorUnit, enums, type: sensorType } = sensor || {};
  const { value } = schedule;
  const isOnOff =
    isSensor &&
    fullSensorTypes?.[sensorType]?.measurementUnits ===
    sensorMeasurementUnits.OpenClose;

  // ---- Common derived ----

  const startHour =
    powerOnTime || powerOnTime === 0 ? minsToTime(powerOnTime, timeFormat) : '';
  const endHour =
    powerOffTime || powerOffTime === 0
      ? minsToTime(powerOffTime, timeFormat)
      : '';

  const displayName =
    isSensor || isProtocolSchedule
      ? schedule.name
      : schedule.name +
      (groupSchedule ? t` - Group` : systemSchedule ? t` -  System` : '');

  const showValueSection = isProtocolSchedule
    ? false
    : isSensor
      ? !isOnOff
      : isGroup
        ? !!setpoint
        : enableSetpointControl && !!setpoint;

  return (
    <div
      className={clsx(classes.scheduleContainer, {
        [classes.notEditable]: notEditable,
      })}
      style={
        isProtocolSchedule
          ? {
            width: (props as BacnetScheduleItemProps | ModbusScheduleItemProps).isDesktop
              ? '49%'
              : '100%',
          }
          : undefined
      }
    >
      <div className={classes.scheduleHeader}>
        <div className={classes.scheduleTitle}>
          <Typography className={classes.longNamestyle}>
            {displayName}
          </Typography>
        </div>
        <div className={classes.scheduleControl}>
          <Tooltip title={t`Edit Schedule`}>
            <IconButton
              disabled={notEditable || !canUpdateSchedules}
              disableRipple={isSensor}
              onClick={() => onEdit(scheduleId)}
              className={classes.iconBtnStyle}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t`Delete Schedule`}>
            <IconButton
              disableRipple
              disabled={notEditable || !canDeleteSchedules}
              onClick={() => onDeleteClick(schedule, type)}
              classes={{ root: classes.overWriteIcon }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <div className={classes.scheduleBody}>
        <div className={classes.rowContainer}>
          <div className={classes.timeRow}>
            <div className={classes.timeSection}>
              <Typography className={classes.timeText}>
                {t`Turn On` + ': ' +
                  (powerOnTime || powerOnTime === 0 ? startHour : `None`)}
              </Typography>
              {!isProtocolSchedule && (
                <Typography className={classes.timeText}>
                  {t`Turn Off` + ': ' +
                    (powerOffTime || powerOffTime === 0 ? endHour : `None`)}
                </Typography>
              )}
            </div>
            {isProtocolSchedule && isSynced === 1 && (
              <div className={classes.syncContainer}>
                <span>{t`Syncing`}</span>
                <Sync />
              </div>
            )}
          </div>
          {showValueSection && (
            <div className={classes.setpointContainer}>
              <div className={classes.setPointSection}>
                {isSensor ? (
                  <Typography className={classes.setPointStyle}>
                    {enums ? enums[value] || value : value}
                    {symbols[sensorUnit || ''] || ''}
                  </Typography>
                ) : (
                  <>
                    <Typography className={classes.setPointStyle}>
                      {isHalfCDegreeEnabled
                        ? Math.floor(setpoint * 2) / 2
                        : Math.round(setpoint)}
                    </Typography>
                    <Typography className={classes.tempSymbolStyle}>
                      {temperatureSymbol()}
                    </Typography>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={classes.bodyRow}>
          {scheduleCategory === scheduleCategories.weekly && (
            <DaysList
              days={Object.keys(weekDays)}
              activeDays={days}
              action={() => { }}
              disablePointer
            />
          )}
          {scheduleCategory === scheduleCategories.calendar && (
            <DatesList dates={dates} dateFormat={dateFormat}/>
          )}
        </div>
        <div className={classes.bodyRow} style={{ padding: '16px 0' }}>
          <Typography className={classes.scheduleStatusStyle}>
            {isDisabled ? t`Status: Inactive` : t`Status: Active`}
          </Typography>
          <div className={classes.controlSec}>
            <GreenSwitch
              disabled={notEditable || !canUpdateSchedules}
              checked={!isDisabled}
              disableRipple={true}
              onChange={(e: any) => onToggle(e, scheduleId)}
              value={true}
              color={theme.palette.colors?.switchPower?.colorChecked || theme.palette.colors?.switch?.colorChecked}
              uncheckedColor={theme.palette.colors?.switchPower?.color || theme.palette.colors?.switch?.color}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleItem;
